import { useState } from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { DateRangePicker } from "@tenant/hotels";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  Icon,
  DialogClose,
  InputNumber,
} from "@tenant/ui";
import clsx from "clsx";
import { usePackageHotelAvailabilitySearch } from "../hooks/usePackageHotelAvailabilitySearch";
import { PackageTravellerPicker } from "../PackageTravellerPicker";
import defaultStyles from "./PackageHotelAvailabilitySearch.module.scss";
import { PackageUpSellBanner } from "./PackageUpSellBanner";
import { useIsUpSell } from "@/overrides/packages/hooks";
import { PackageLocationAutocomplete } from "@/pages/Packages/components";

const COMPONENT_KEY = CoreHotelsComponent.HotelAvailabilitySearch;

export type PackageHotelAvailabilitySearchProps = PlatformComponentProps;

/**
 * Custom HotelAvailabilitySearch component for packages that uses the
 * packages requisition endpoint when search parameters change.
 */
export const PackageHotelAvailabilitySearch = ({
  children,
  className,
}: PackageHotelAvailabilitySearchProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const isUpSell = useIsUpSell();
  const [showSearch, setShowSearch] = useState(false);
  const { context, handlers, text } = usePackageHotelAvailabilitySearch();

  if (isUpSell && !showSearch) {
    return <PackageUpSellBanner onStartNewSearch={() => setShowSearch(true)} />;
  }

  const search = () => (
    <div className={cn("search-wrapper")}>
      {context.shouldShowOrigin && (
        <div className={cn("origin")}>
          <PackageLocationAutocomplete
            label={text.originLabel}
            value={context.origin}
            onChange={(val) => handlers.handleOriginChange(val)}
            id={"package-origin-autocomplete"}
            density="search"
          />
        </div>
      )}
      <div className={cn("destination")}>
        <PackageLocationAutocomplete
          label={text.locationLabel}
          value={context.destination}
          onChange={(val) => handlers.handlePlaceChange(val)}
          id={"package-location-autocomplete"}
          density="search"
        />
      </div>
      <div className={cn("dates")}>
        <DateRangePicker
          startDate={context.dateRange?.from}
          endDate={context.dateRange?.until}
          onStartDateChangeHandler={(date) => {
            handlers.handleDateRangeFromChange(date);
          }}
          onEndDateChangeHandler={(date) => {
            handlers.handleDateRangeUntilChange(date);
          }}
          onClose={() => handlers.handleDateRangeClose()}
        />
      </div>
      <div className={cn("guests")}>
        <PackageTravellerPicker
          guests={context.packageGuests}
          onGuestsChange={handlers.handleGuestsChange}
          showInfants={context.packageTypeIncludesFlights}
        />
      </div>
      {context.shouldShowDriverAge && (
        <div className={cn("driver-age")}>
          <InputNumber
            startContent={<Icon iconName={IconNameEnum.guest} />}
            label={text.driverAgeLabel}
            value={context.driverAge ?? ""}
            onChange={(e) => handlers.handleDriverAgeChange(e.target.value)}
            placeholder={text.driverAgePlaceholder}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className={clsx(block, className)}>
      <h1 className={cn("title")}>{text.title}</h1>
      <div className={cn("desktop-search")}>{search()}</div>
      <Dialog
        modal={false}
        onOpen={handlers.onDialogOpen}
        onClose={handlers.onDialogClose}
      >
        <DialogTrigger className={cn("search-button")}>
          <div className={cn("icon")}>
            <Icon iconName={IconNameEnum.search} />
          </div>
          <div className={cn("content")}>
            <div className={cn("search-button-label")}>{text.locationData}</div>
            <div className={cn("search-button-value")}>
              {text.searchSummary}
            </div>
          </div>
        </DialogTrigger>
        <DialogContent
          fullScreen
          title={text.title}
          titleIsVisuallyHidden
          bottomFixedContent={
            <DialogClose asChild>
              <button className={cn("submit-search-button")}>
                {text.search}
              </button>
            </DialogClose>
          }
        >
          {search()}
        </DialogContent>
      </Dialog>
      {children}
    </div>
  );
};
